import _ from 'underscore';
import React, { useCallback, useMemo, useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  GenericAdapter,
  type ValidationProps,
  withValidation,
} from '@bingads-webui-react/validation';
import { withErrorField } from '@bingads-webui-react/primitive-validation-fluent';
import { mergeClasses } from '@fluentui/react-components';

import { BudgetInputPure, BudgetInputPureProps } from './budget-input-pure';
import { useBudgetRange } from './use-budget-range';
import { useValidatedBudgetInputStyles } from './styles/validated-budget-input-styles';

const BudgetInput = withErrorField(withValidation(BudgetInputPure, GenericAdapter));
const MAX_BUDGET_DECIMAL_LENGTH = 2;

declare function _TL_(arg0: string): string;

export interface BudgetValidationResult {
  data: Record<string, unknown>;
  messages?: string[];
  isDirty?: boolean;
  isValid: boolean;
}

export type BudgetValidationErrors = Record<string, string>;
export type BudgetDefaultValidator = (value: unknown) => BudgetValidationErrors;

export interface ValidatedBudgetInputValidationProps extends ValidationProps {
  onValidate?: (result: BudgetValidationResult) => void;
  passiveMessages?: string[];
  showSingleError?: boolean;
  validator?: (
    value: unknown,
    defaultValidator: BudgetDefaultValidator
  ) => BudgetValidationErrors;
  [key: string]: unknown;
}

export interface ValidatedBudgetInputProps extends Omit<BudgetInputPureProps, 'currency' | 'onChange'> {
  budgetKey?: string;
  maxBudget?: number;
  minBudget?: number;
  currency?: string;
  i18n?: any;
  validationProps?: ValidatedBudgetInputValidationProps;
}

const getDecimalSeparator = (i18n: any): string => {
  const formattedDecimal = String(i18n.formatDecimal(1.1, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }));
  return Array.from(formattedDecimal).find(character =>
    !/[\p{Number}\p{Separator}\p{Format}]/u.test(character)) || '.';
};

const hasExcessDecimalLength = (value: unknown, i18n: any): boolean => {
  if (typeof value !== 'string' || !i18n.isValidDecimal(value, true)) {
    return false;
  }
  const decimalSeparator = getDecimalSeparator(i18n);
  const decimalIndex = value.lastIndexOf(decimalSeparator);
  return decimalIndex >= 0
    && Array.from(value.slice(decimalIndex + decimalSeparator.length)).length
      > MAX_BUDGET_DECIMAL_LENGTH;
};

export const ValidatedBudgetInput: React.FC<ValidatedBudgetInputProps> = (props) => {
  const {
    currency,
    budgetKey,
    maxBudget,
    minBudget,
    i18n: providedI18n,
    validationProps = {},
    ...rest
  } = props;

  const classes = useValidatedBudgetInputStyles();
  const contextI18n = useContext(I18nContext) as any;
  const i18n = providedI18n || contextI18n;

  const {
    currencyCode,
    minDailyBudget,
    maxDailyBudget,
  } = useBudgetRange();

  const finalMaxBudget = maxBudget || maxDailyBudget;
  const finalMinBudget = minBudget || minDailyBudget;
  const finalCurrency = currency || currencyCode;
  const validationSourceI18n = validationProps.i18n || i18n;

  const validationMessages = useMemo<Record<string, string | undefined>>(() => ({
    decimalLength: validationSourceI18n.getString(_TL_('Budget needs to be set to 2 decimal lengths')),
    invalid: validationSourceI18n.getString(_TL_('Field is not valid')),
    required: validationSourceI18n.getString(_TL_('This field is required')),
    type: validationSourceI18n.getString(_TL_('Field requires numeric value')),
    range: Number.isFinite(finalMinBudget)
      ? validationSourceI18n.getString(_TL_('Your budget should be greater than {{accountCurrencyCode}} {{dailyRangeFloorValue}} and less than your daily maximum. The daily max. is your monthly budget averaged out over the number of days left in the month.'), {
        accountCurrencyCode: finalCurrency,
        dailyRangeFloorValue: validationSourceI18n.formatDecimal(finalMinBudget),
      })
      : undefined,
  }), [finalCurrency, finalMinBudget, validationSourceI18n]);
  const validationI18n = useMemo(() => {
    const legacyMessages: Record<string, string | undefined> = {
      Validation_Base_Field_Not_Valid: validationMessages.invalid,
      Validation_NotEmpty_Required_Field: validationMessages.required,
      Validation_Number_Require_Numeric: validationMessages.type,
      Validation_Number_Max_Length: validationMessages.decimalLength,
      Validation_Number_Range_Between: validationMessages.range,
      Validation_Number_Range_Min: validationMessages.range,
      Validation_Number_Range_Max: validationMessages.range,
    };

    return {
      getString: (key: string, model?: Record<string, unknown>) =>
        legacyMessages[key] || validationSourceI18n.getString(key, model),
      formatDecimal: validationSourceI18n.formatDecimal.bind(validationSourceI18n),
      isValidDecimal: validationSourceI18n.isValidDecimal.bind(validationSourceI18n),
      parseDecimal: validationSourceI18n.parseDecimal.bind(validationSourceI18n),
    };
  }, [validationMessages, validationSourceI18n]);

  const budgetSchema = useMemo(() => ({
    name: 'Budget schema',
    type: 'object',
    properties: {
      [String(budgetKey)]: {
        type: 'number',
        required: true,
        maximum: finalMaxBudget,
        minimum: finalMinBudget,
      },
    },
  }), [budgetKey, finalMaxBudget, finalMinBudget]);

  const customErrorProcessor = useCallback((errors: Record<string, string>) => _.map(
    errors,
    (oldError, key) => validationMessages[key] || oldError
  ), [validationMessages]);
  const customValidator = validationProps.validator;
  const budgetValidator = useCallback(
    (value: unknown, defaultValidator: BudgetDefaultValidator) => {
      const errors = customValidator
        ? customValidator(value, defaultValidator)
        : defaultValidator(value);
      if (hasExcessDecimalLength(value, i18n)) {
        errors.decimalLength = errors.decimalLength || 'decimalLength';
      }
      return errors;
    },
    [customValidator, i18n]
  );

  return (
    <div className={mergeClasses('validated-budget-input-container', classes.root)}>
      <I18nContext.Provider value={i18n}>
        <BudgetInput
          validationProps={{
            schema: budgetSchema,
            field: budgetKey,
            errorProcessor: customErrorProcessor,
            ...validationProps,
            i18n: validationI18n,
            validator: budgetValidator,
          }}
          currency={finalCurrency}
          key={`${finalCurrency}-${finalMaxBudget}-${finalMinBudget}`}
          {...rest}
        />
      </I18nContext.Provider>
    </div>
  );
};

ValidatedBudgetInput.defaultProps = {
  budgetKey: 'Budget',
  maxBudget: undefined,
  minBudget: undefined,
  currency: undefined,
  i18n: undefined,
  validationProps: {},
};
